import { useSession } from 'next-auth/react';
import { useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import useSWRInfinite from 'swr/infinite';

import { DURATION, NEXT_AUTH_STATUSES } from '@/lib/constants';
import { FullUserNotificationModel } from '@/lib/models/notification/types';
import {
  userNotificationsErrorState,
  userNotificationsIsLoadingState,
  userNotificationsLoadMoreState,
  userNotificationsMarkAllAsReadState,
  userNotificationsMarkAsReadState,
  userNotificationsSelectorState,
} from '@/state/user-notifications';
import {
  UserNotificationReadApiResponse,
  UserNotificationsAllReadApiResponse,
  UserNotificationsApiResponse,
} from '@/types/api-types';
import { request } from '@/utils/api/request';
import {
  getUserNotificationAllReadUrl,
  getUserNotificationReadUrl,
  getUserNotificationsUrl,
} from '@/utils/helpers/api-urls';
import { logEvent } from '@/utils/logger';

const ERROR_MESSAGE = 'Error fetching notifications';

const REFRESH_INTERVAL =
  process.env.NEXT_PUBLIC_NOTIFICATIONS_REFRESH_INTERVAL || DURATION.IN_MILLISECONDS.THIRTY_SECONDS;

export const useUserNotifications = ({ isEnabled }: { isEnabled?: boolean }) => {
  const { status } = useSession();
  const isAuthenticated = status === NEXT_AUTH_STATUSES.AUTHENTICATED;
  const setUserNotificationsState = useSetRecoilState(userNotificationsSelectorState);
  const setUserNotificationsIsLoadingState = useSetRecoilState(userNotificationsIsLoadingState);
  const setUserNotificationsErrorState = useSetRecoilState(userNotificationsErrorState);
  const setUserNotificationsLoadMoreState = useSetRecoilState(userNotificationsLoadMoreState);
  const setUserNotificationsMarkAsReadState = useSetRecoilState(userNotificationsMarkAsReadState);
  const setUserNotificationsMarkAllAsReadState = useSetRecoilState(userNotificationsMarkAllAsReadState);

  const getUrl = (pageIndex: number) => {
    return `${getUserNotificationsUrl()}?pageIndex=${pageIndex}`;
  };

  const { data, isLoading, error, setSize, size, mutate } = useSWRInfinite<UserNotificationsApiResponse>(
    // @ts-expect-error type for useSWRInfinite appears to be incorrect https://github.com/vercel/swr/issues/936#issuecomment-812996438
    isEnabled && isAuthenticated ? getUrl : null,
    request,
    { keepPreviousData: true, errorRetryCount: 2, refreshInterval: REFRESH_INTERVAL },
  );

  const handleLoadMore = () => {
    setSize(size + 1);
  };

  const markAsRead = async (id: string, isRead: boolean) => {
    const optimisticData: UserNotificationsApiResponse[] | undefined = data?.map((page) => {
      return {
        ...page,
        unreadCount: page?.unreadCount !== undefined ? page.unreadCount + (isRead ? -1 : 1) : page.unreadCount,
        notifications: page.notifications?.map((notification) => ({
          ...notification,
          isRead: notification.id === id ? isRead : notification.isRead,
        })),
      };
    });
    await mutate(
      async () => {
        const result = await request<UserNotificationReadApiResponse>(
          getUserNotificationReadUrl(id),
          { body: { isRead } },
          'PATCH',
        );
        if (result.error) {
          logEvent.error('Error marking as read', { error: result.error });
        }
        return optimisticData;
      },
      { optimisticData },
    );
  };

  const markAllAsRead = async () => {
    const optimisticData: UserNotificationsApiResponse[] | undefined = data?.map((page) => {
      return {
        ...page,
        unreadCount: 0,
        notifications: page.notifications?.map((notification) => ({ ...notification, isRead: true })),
      };
    });
    await mutate(
      async () => {
        const result = await request<UserNotificationsAllReadApiResponse>(
          getUserNotificationAllReadUrl(),
          undefined,
          'PATCH',
        );
        if (result.error) {
          logEvent.error('Error marking all as read', { error: result.error });
        }
        return optimisticData;
      },
      { optimisticData },
    );
  };

  const count = data?.length ? data[0].count : undefined;
  const unreadCount = data?.length ? data[0].unreadCount : undefined;
  const notifications = data?.length
    ? data.reduce((acc, item) => [...acc, ...(item.notifications || [])], [] as FullUserNotificationModel[])
    : undefined;

  const errorMessage = error ? ERROR_MESSAGE : undefined;

  useEffect(() => {
    setUserNotificationsIsLoadingState(isLoading);
  }, [isLoading]);

  useEffect(() => {
    setUserNotificationsErrorState(errorMessage);
  }, [errorMessage]);

  useEffect(() => {
    setUserNotificationsMarkAsReadState(() => markAsRead);
    setUserNotificationsMarkAllAsReadState(() => markAllAsRead);
  }, [mutate, data]);

  useEffect(() => {
    setUserNotificationsLoadMoreState(() => handleLoadMore);
  }, [size, setSize]);

  useEffect(() => {
    setUserNotificationsState({ count, notifications, unreadCount });
  }, [count, notifications, unreadCount]);
};
